import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { getAiVisCssVars } from './aivisTheme';
import './ai-vis.css';

export default function AiVisResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const uid = searchParams.get('uid') || '';
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const invalidLink = !uid || !token;

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/aivis/auth/reset-password/', {
        uid,
        token,
        password,
        confirm_password: confirmPassword,
      });
      navigate('/ai-vis/login', { state: { invited: true }, replace: true });
    } catch (err) {
      const d = err.response?.data;
      setError(d?.detail || 'Reset failed. The link may have expired — request a new one.');
    } finally {
      setBusy(false);
    }
  }

  function EyeToggle({ show, onToggle }) {
    return (
      <button
        type="button"
        aria-label={show ? 'Hide password' : 'Show password'}
        onClick={onToggle}
        style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          color: 'var(--aivis-text-muted)', display: 'flex', alignItems: 'center',
        }}
      >
        {show ? (
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        ) : (
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        )}
      </button>
    );
  }

  return (
    <div className="aivis-auth-page" style={getAiVisCssVars()}>
      <div className="aivis-auth-card">
        <h1>AI-Vis</h1>
        <p>Choose a new password</p>

        {invalidLink ? (
          <>
            <div className="aivis-auth-error">
              Invalid or missing reset link. Please request a new one.
            </div>
            <div className="aivis-auth-link">
              <Link to="/ai-vis/forgot-password">Request new link</Link>
            </div>
          </>
        ) : (
          <>
            {error && <div className="aivis-auth-error">{error}</div>}
            <form onSubmit={onSubmit}>
              <div className="aivis-field">
                <label htmlFor="aivis-reset-password">New password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="aivis-reset-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                    style={{ paddingRight: 40 }}
                  />
                  <EyeToggle show={showPassword} onToggle={() => setShowPassword((v) => !v)} />
                </div>
              </div>
              <div className="aivis-field">
                <label htmlFor="aivis-reset-confirm">Confirm new password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="aivis-reset-confirm"
                    type={showConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat your new password"
                    required
                    style={{ paddingRight: 40 }}
                  />
                  <EyeToggle show={showConfirm} onToggle={() => setShowConfirm((v) => !v)} />
                </div>
              </div>
              <button type="submit" className="aivis-btn-primary" disabled={busy}>
                {busy ? 'Saving…' : 'Set new password'}
              </button>
            </form>
            <div className="aivis-auth-link">
              <Link to="/ai-vis/login">Back to sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
